import { ConfigurationImpl as Configuration } from '@/api/Configuration';
import { HttpStatus } from '@/api/HttpStatus';
import {
  toApiOrderCreate,
  toApiOrderStatus,
  toDailyOrderMetricsResult,
  toMonthlyOrderMetricsResult,
  toOrder,
  toOrders,
} from '@/api/mappers/mapOrder';
import { toOrderAttentionItems } from '@/api/mappers/mapOrderAttention';
import { toApiOrderIncompleteDeliveryCreateRequest } from '@/api/mappers/mapOrderIncompleteDelivery';
import {
  toApiMinimumOverdueDays,
  toApiStoreVarianceThresholdPercent,
  toApiSupplierStoreVarianceThresholdPercent,
  toStoreOrderVariances,
  toSupplierStoreOrderVariances,
} from '@/api/mappers/mapOrderVariance';
import {
  AcceptOrderRequest,
  OrdersApi as ApiOrdersApi,
  BulkCreateOrdersRequest,
  CancelOrderRequest,
  CompleteOrderRequest,
  CreateOrderRequest,
  DispatchOrderRequest,
  ExportOrdersRequest,
  GetOrderDetailsRequest,
  ListDailyOrderMetricsRequest,
  ListMonthlyOrderMetricsRequest,
  ListOrderAttentionItemsRequest,
  ListOrdersRequest,
  ListStoreOrderVariancesRequest,
  ListSupplierStoreOrderVariancesRequest,
  ReceiveOrderRequest,
  RejectOrderRequest,
  ReportOrderIncompleteDeliveryRequest,
  ResponseError,
  SubmitOrderRequest,
  UpdateOrderRequest,
} from '@/gen/api';
import { GoodsReceivedNote } from '@/models/GoodsReceivedNote';
import {
  Order,
  OrderAssociation,
  OrderCreate,
  OrderCreateResult,
  Orders,
  OrderUpdate,
} from '@/models/Order';
import { OrderAttentionItems } from '@/models/OrderAttention';
import {
  OrderIncompleteDeliveryAlreadyReportedError,
  OrderIncompleteDeliveryReport,
} from '@/models/OrderIncompleteDelivery';
import { DailyOrderMetricsResult, MonthlyOrderMetricsResult } from '@/models/OrderMetrics';
import { OrderStatus } from '@/models/OrderStatus';
import {
  MinimumOverdueDays,
  StoreOrderVariances,
  SupplierStoreOrderVariances,
  VarianceThresholdPercent,
} from '@/models/OrderVariance';
import { TimeWindow } from '@/models/TimeWindow';

export interface OrderFilters {
  storeId?: string;
  supplierId?: string;
  status?: string | OrderStatus;
  period?: TimeWindow;
}

export interface OrderVarianceFilters {
  storeId?: string;
  supplierId?: string;
  varianceThresholdPercent?: VarianceThresholdPercent;
}

export interface SupplierStoreOrderVarianceFilters extends OrderVarianceFilters {
  minimumOverdueDays?: MinimumOverdueDays;
}

interface OrdersApi {
  listOrders(offset?: number, limit?: number, include?: OrderAssociation[]): Promise<Orders>;
  findOrders(
    query?: string | null,
    filters?: OrderFilters,
    offset?: number,
    limit?: number,
    include?: OrderAssociation[],
  ): Promise<Orders>;
  getOrder(idOrCode: string, include?: OrderAssociation[]): Promise<Order | undefined>;
  bulkCreateOrders(orders: OrderCreate[]): Promise<OrderCreateResult[]>;
  createOrder(order: OrderCreate): Promise<OrderCreateResult>;
  updateOrder(id: string, order: OrderUpdate): Promise<void>;
  submitOrder(id: string): Promise<void>;
  acceptOrder(id: string): Promise<void>;
  dispatchOrder(id: string): Promise<void>;
  receiveOrder(id: string): Promise<void>;
  rejectOrder(id: string, reason: string): Promise<void>;
  cancelOrder(id: string): Promise<void>;
  completeOrder(id: string): Promise<void>;
  addProofOfPayment(id: string, image: string): Promise<void>;
  submitGoodsReceivedNote(id: string, goodsReceivedNote: GoodsReceivedNote): Promise<void>;
  exportOrders(
    query?: string | null,
    filters?: OrderFilters,
    orderIds?: string[],
    offset?: number,
    limit?: number,
    format?: 'csv' | 'xlsx',
  ): Promise<Blob>;
  listDailyOrderMetrics(
    start: Date,
    end: Date,
    filters?: OrderFilters,
    currencyCode?: string,
  ): Promise<DailyOrderMetricsResult | undefined>;
  listMonthlyOrderMetrics(
    start: Date,
    end: Date,
    filters?: OrderFilters,
    currencyCode?: string,
  ): Promise<MonthlyOrderMetricsResult | undefined>;
  listSupplierStoreOrderVariances(
    filters?: SupplierStoreOrderVarianceFilters,
    offset?: number,
    limit?: number,
  ): Promise<SupplierStoreOrderVariances>;
  listStoreOrderVariances(
    orderIds?: string[],
    filters?: OrderVarianceFilters,
    offset?: number,
    limit?: number,
  ): Promise<StoreOrderVariances>;
  listOrderAttentionItems(
    filters?: OrderFilters,
    offset?: number,
    limit?: number,
  ): Promise<OrderAttentionItems>;
  reportOrderIncompleteDelivery(
    orderId: string,
    report: OrderIncompleteDeliveryReport,
  ): Promise<void>;
  resolveOrderIncompleteDelivery(orderId: string, incompleteDeliveryId: string): Promise<void>;
}

export class OrdersApiImpl implements OrdersApi {
  private configuration: Configuration;
  private ordersApi: ApiOrdersApi;

  constructor(configuration: Configuration) {
    this.configuration = configuration;
    this.ordersApi = new ApiOrdersApi(configuration);
  }

  async listOrders(offset?: number, limit?: number, include?: OrderAssociation[]): Promise<Orders> {
    const requestParams: ListOrdersRequest = {
      offset,
      limit,
      include: include?.map((value) => value.toString()),
    };

    try {
      const response = await this.ordersApi.listOrders(requestParams);
      return toOrders(response);
    } catch (error) {
      console.error('Error listing orders:', error);
      throw error;
    }
  }

  async findOrders(
    query?: string | null,
    filters?: OrderFilters,
    offset?: number,
    limit?: number,
    include?: OrderAssociation[],
  ): Promise<Orders> {
    const requestParams: ListOrdersRequest = {
      query: query || '',
      offset,
      limit,
      include: include?.map((value) => value.toString()),
      status: filters?.status ? toApiOrderStatus(filters?.status) : undefined,
      storeId: filters?.storeId ?? undefined,
      supplierId: filters?.supplierId ?? undefined,
      start: filters?.period?.start ?? undefined,
      end: filters?.period?.end ?? undefined,
    };

    try {
      const response = await this.ordersApi.listOrders(requestParams);
      return toOrders(response);
    } catch (error) {
      console.error('Error finding orders:', error);
      throw error;
    }
  }

  async getOrder(idOrCode: string, include?: OrderAssociation[]): Promise<Order | undefined> {
    try {
      const requestParameters: GetOrderDetailsRequest = {
        id: idOrCode,
        include: include?.map((value) => value.toString()),
      };

      const response = await this.ordersApi.getOrderDetails(requestParameters);
      return toOrder(response);
    } catch (error) {
      console.error(`Failed to get order with id ${idOrCode}:`, error);
      return undefined;
    }
  }

  async createOrder(order: OrderCreate): Promise<OrderCreateResult> {
    try {
      const requestParameters: CreateOrderRequest = {
        orderCreateRequest: toApiOrderCreate(order),
      };

      return await this.ordersApi.createOrder(requestParameters);
    } catch (error) {
      console.error('Failed to create order:', error);
      throw error;
    }
  }

  async bulkCreateOrders(orders: OrderCreate[]): Promise<OrderCreateResult[]> {
    try {
      const bulkOrderCreateRequest: BulkCreateOrdersRequest = {
        bulkOrderCreateRequest: {
          orders: orders.map(toApiOrderCreate),
        },
      };

      const response = await this.ordersApi.bulkCreateOrders(bulkOrderCreateRequest);
      return response.orders;
    } catch (error) {
      console.error('Failed to create bulk orders:', error);
      throw error;
    }
  }

  async updateOrder(id: string, order: OrderUpdate): Promise<void> {
    try {
      const requestParameters: UpdateOrderRequest = {
        id,
        orderUpdateRequest: {
          confirmedDeliveryWindow: order.confirmedDeliveryWindow,
          items: order.items,
        },
      };

      await this.ordersApi.updateOrder(requestParameters);
    } catch (error) {
      console.error('Failed to update order:', error);
      throw error;
    }
  }

  async submitOrder(id: string): Promise<void> {
    try {
      const requestParameters: SubmitOrderRequest = { id };

      await this.ordersApi.submitOrder(requestParameters);
    } catch (error) {
      console.error('Failed to submit order:', error);
      throw error;
    }
  }

  async acceptOrder(id: string): Promise<void> {
    try {
      const requestParameters: AcceptOrderRequest = { id };

      await this.ordersApi.acceptOrder(requestParameters);
    } catch (error) {
      console.error('Failed to accept order:', error);
      throw error;
    }
  }

  async dispatchOrder(id: string): Promise<void> {
    try {
      const requestParameters: DispatchOrderRequest = { id };

      await this.ordersApi.dispatchOrder(requestParameters);
    } catch (error) {
      console.error('Failed to dispatch order:', error);
      throw error;
    }
  }

  async receiveOrder(id: string): Promise<void> {
    try {
      const requestParameters: ReceiveOrderRequest = { id };

      await this.ordersApi.receiveOrder(requestParameters);
    } catch (error) {
      console.error('Failed to receive order:', error);
      throw error;
    }
  }

  async rejectOrder(id: string, reason: string): Promise<void> {
    try {
      const requestParameters: RejectOrderRequest = {
        id,
        orderReject: { reason },
      };

      await this.ordersApi.rejectOrder(requestParameters);
    } catch (error) {
      console.error('Failed to reject order:', error);
      throw error;
    }
  }

  async cancelOrder(id: string): Promise<void> {
    try {
      const requestParameters: CancelOrderRequest = { id };

      await this.ordersApi.cancelOrder(requestParameters);
    } catch (error) {
      console.error('Failed to cancel order:', error);
      throw error;
    }
  }

  async completeOrder(id: string): Promise<void> {
    try {
      const requestParameters: CompleteOrderRequest = { id };

      await this.ordersApi.completeOrder(requestParameters);
    } catch (error) {
      console.error('Failed to complete order:', error);
      throw error;
    }
  }

  async exportOrders(
    query?: string | null,
    filters?: OrderFilters,
    orderIds?: string[],
    offset?: number,
    limit?: number,
    format = 'csv',
  ): Promise<Blob> {
    const { supplierId, status } = filters || {};

    try {
      const requestParameters: ExportOrdersRequest = {
        query: query ?? undefined,
        status: status ? toApiOrderStatus(status) : undefined,
        supplierId,
        offset,
        limit,
        orderExportRequest: {
          orderIds,
        },
      };

      const acceptHeader =
        format === 'csv'
          ? 'text/csv'
          : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

      return await this.ordersApi.exportOrders(requestParameters, {
        headers: {
          ...this.configuration.headers,
          Accept: acceptHeader,
        },
      });
    } catch (error) {
      console.error('Failed to export orders:', error);
      throw error;
    }
  }

  addProofOfPayment(_id: string, _image: string): Promise<void> {
    return Promise.resolve(undefined);
  }

  submitGoodsReceivedNote(_id: string, _goodsReceivedNote: GoodsReceivedNote): Promise<void> {
    return Promise.resolve(undefined);
  }

  async listDailyOrderMetrics(
    start: Date,
    end: Date,
    filters?: OrderFilters,
    currencyCode?: string,
  ): Promise<DailyOrderMetricsResult | undefined> {
    const requestParams: ListDailyOrderMetricsRequest = {
      start: start,
      end: end,
      currencyCode: currencyCode || undefined,
      status: filters?.status ? toApiOrderStatus(filters.status) : undefined,
      storeId: filters?.storeId ?? undefined,
      supplierId: filters?.supplierId ?? undefined,
    };

    try {
      const response = await this.ordersApi.listDailyOrderMetrics(requestParams);
      return toDailyOrderMetricsResult(response);
    } catch (error) {
      console.error('Error listing daily order metrics:', error);
      throw error;
    }
  }

  async listMonthlyOrderMetrics(
    start: Date,
    end: Date,
    filters?: OrderFilters,
    currencyCode?: string,
  ): Promise<MonthlyOrderMetricsResult | undefined> {
    const requestParams: ListMonthlyOrderMetricsRequest = {
      start: start,
      end: end,
      currencyCode: currencyCode || undefined,
      status: filters?.status ? toApiOrderStatus(filters.status) : undefined,
      storeId: filters?.storeId ?? undefined,
      supplierId: filters?.supplierId ?? undefined,
    };

    try {
      const response = await this.ordersApi.listMonthlyOrderMetrics(requestParams);
      return toMonthlyOrderMetricsResult(response);
    } catch (error) {
      console.error('Error listing monthly order metrics:', error);
      throw error;
    }
  }

  async listSupplierStoreOrderVariances(
    filters?: SupplierStoreOrderVarianceFilters,
    offset?: number,
    limit?: number,
  ): Promise<SupplierStoreOrderVariances> {
    const requestParams: ListSupplierStoreOrderVariancesRequest = {
      supplierId: filters?.supplierId,
      storeId: filters?.storeId,
      varianceThresholdPercent: toApiSupplierStoreVarianceThresholdPercent(
        filters?.varianceThresholdPercent,
      ),
      minimumOverdueDays: toApiMinimumOverdueDays(filters?.minimumOverdueDays),
      offset,
      limit,
    };

    try {
      const response = await this.ordersApi.listSupplierStoreOrderVariances(requestParams);
      return toSupplierStoreOrderVariances(response);
    } catch (error) {
      console.error('Error listing supplier/store order variances:', error);
      throw error;
    }
  }

  async listStoreOrderVariances(
    orderIds?: string[],
    filters?: OrderVarianceFilters,
    offset?: number,
    limit?: number,
  ): Promise<StoreOrderVariances> {
    const requestParams: ListStoreOrderVariancesRequest = {
      orderId: orderIds,
      supplierId: filters?.supplierId,
      storeId: filters?.storeId,
      varianceThresholdPercent: toApiStoreVarianceThresholdPercent(
        filters?.varianceThresholdPercent,
      ),
      offset,
      limit,
    };

    try {
      const response = await this.ordersApi.listStoreOrderVariances(requestParams);
      return toStoreOrderVariances(response);
    } catch (error) {
      console.error('Error listing store/order variances:', error);
      throw error;
    }
  }

  async listOrderAttentionItems(
    filters?: OrderFilters,
    offset?: number,
    limit?: number,
  ): Promise<OrderAttentionItems> {
    const requestParams: ListOrderAttentionItemsRequest = {
      supplierId: filters?.supplierId,
      storeId: filters?.storeId,
      offset,
      limit,
    };

    try {
      const response = await this.ordersApi.listOrderAttentionItems(requestParams);
      return toOrderAttentionItems(response);
    } catch (error) {
      console.error('Error listing order attention items:', error);
      throw error;
    }
  }

  async reportOrderIncompleteDelivery(
    orderId: string,
    report: OrderIncompleteDeliveryReport,
  ): Promise<void> {
    const requestParams: ReportOrderIncompleteDeliveryRequest = {
      id: orderId,
      orderIncompleteDeliveryCreateRequest: toApiOrderIncompleteDeliveryCreateRequest(report),
    };

    try {
      await this.ordersApi.reportOrderIncompleteDelivery(requestParams);
    } catch (error) {
      if (error instanceof ResponseError && error.response.status === HttpStatus.Conflict) {
        throw new OrderIncompleteDeliveryAlreadyReportedError(orderId);
      }

      console.error('Error reporting incomplete delivery:', error);
      throw error;
    }
  }

  async resolveOrderIncompleteDelivery(
    orderId: string,
    incompleteDeliveryId: string,
  ): Promise<void> {
    try {
      await this.ordersApi.resolveOrderIncompleteDelivery({
        id: orderId,
        incompleteDeliveryId,
        orderIncompleteDeliveryResolveRequest: {},
      });
    } catch (error) {
      console.error('Error resolving incomplete delivery:', error);
      throw error;
    }
  }
}

export default OrdersApi;
